import { useRef } from 'react';
import { createUuid } from '@notifi/uuid/browser';
import { useAuthStore } from '@/stores/auth.store';
import type { TermsMode } from '@/lib/terms';
import { getActiveTenantQueryKey } from '@/lib/activeTenant';
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api/client';
import { toast } from 'sonner';

export type OrderStatus =
  | 'DRAFT'
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'ORDERED'
  | 'PARTIALLY_RECEIVED'
  | 'RECEIVED'
  | 'CANCELED'
  | 'CLOSED';
export type SupplierAckFilter = 'AWAITING' | 'ACKNOWLEDGED' | 'CHANGES_PROPOSED' | 'REJECTED';
export const supplierAckFilters: Record<SupplierAckFilter, string> = {
  AWAITING: 'Awaiting the supplier',
  ACKNOWLEDGED: 'Acknowledged by supplier',
  CHANGES_PROPOSED: 'Supplier proposed changes',
  REJECTED: 'Declined by supplier',
};
export const statuses: Record<OrderStatus, string> = {
  DRAFT: 'Draft',
  PENDING_APPROVAL: 'Waiting for approval',
  APPROVED: 'Approved',
  ORDERED: 'Ordered',
  PARTIALLY_RECEIVED: 'Partially received',
  RECEIVED: 'Received',
  CANCELED: 'Canceled',
  CLOSED: 'Closed',
};
export interface OrderLine {
  purchaseUnit: string;
  stockUnit: string;
  unitsPerPurchaseUnit: number;
  /** Freight this line carries into stock cost; null: its share by value. */
  landedChargeOverride?: number | null;
  manufacturerPartNumber?: string | null;
  id: string;
  name: string;
  sku: string | null;
  supplierPartNumber: string | null;
  catalogUnitCost: number | null;
  itemId: string | null;
  partRequirementId?: string | null;
  quantity: number;
  unitCost: number;
  receivedQuantity: number;
  canceledQuantity: number;
  cancellationReason: string | null;
  /** Billed through approved supplier bills or matched card expenses. */
  billedQuantity?: number;
  /** The job this line is bought for when not the order's (PUR-PO-9). */
  jobId?: string | null;
  job?: { id: string; jobNumber: string; name: string } | null;
  /** The supplier's delivery promise and what it has on backorder. */
  promisedAt?: string | null;
  backorderedQuantity?: number;
  /** Set when the line was bought into a stock location of its own. */
  destinationLocationId?: string | null;
  item?: { trackInventory: boolean; trackSerials: boolean; imageKey?: string | null } | null;
  /** The item's photo, signed for this page; null when it has none. */
  imagePreviewUrl?: string | null;
}
/** What a change did to one line; money is absent when costs are hidden. */
export interface RevisionLineState {
  quantity: string;
  receivedQuantity: string;
  canceledQuantity: string;
  unitCost?: string;
  supplierPartNumber: string | null;
  manufacturerPartNumber: string | null;
  jobId: string | null;
  promisedAt: string | null;
  backorderedQuantity: string;
}
export interface RevisionLineUpdate {
  quantity?: string;
  unitCost?: string;
  name?: string;
  supplierPartNumber?: string | null;
  manufacturerPartNumber?: string | null;
  jobId?: string | null;
  promisedAt?: string | null;
  backorderedQuantity?: string;
}
export type RevisionLineChange =
  | {
      kind: 'CHANGE';
      lineId: string;
      name: string;
      before: RevisionLineState;
      after: RevisionLineUpdate;
    }
  | { kind: 'REMOVE'; lineId: string; name: string; before: RevisionLineState }
  | {
      kind: 'ADD';
      line: {
        name: string;
        quantity: string;
        unitCost?: string;
        purchaseUnit: string;
        supplierPartNumber: string | null;
        jobId: string | null;
        replacesLineId: string | null;
      };
    };
export interface RevisionChangeSet {
  lines: RevisionLineChange[];
  /** Absent when costs are hidden from the viewer. */
  charges?: Partial<
    Record<'shipping' | 'tax' | 'otherCharges' | 'discount', { before: string; after: string }>
  >;
}
export interface SupplierProposalLine {
  lineId: string;
  name: string;
  before: RevisionLineState;
  quantity?: string;
  unitCost?: string;
  promisedAt?: string;
  backorderedQuantity?: string;
  substitute?: {
    name: string;
    supplierPartNumber: string | null;
    manufacturerPartNumber: string | null;
  };
  note?: string | null;
}
export type RevisionStatus =
  | 'PROPOSED'
  | 'PENDING_APPROVAL'
  | 'APPLIED'
  | 'REJECTED'
  | 'SUPERSEDED';
/** A change order or a supplier's proposal (PUR-PO-24, -25). */
export interface OrderRevision {
  id: string;
  revisionNumber: number | null;
  proposedBy: 'BUYER' | 'SUPPLIER';
  status: RevisionStatus;
  reason: string | null;
  /** A buyer's change; for a supplier proposal, what they proposed. */
  changes: RevisionChangeSet | { lines: SupplierProposalLine[] };
  /** What was accepted and applied, or waits on approval. */
  accepted: RevisionChangeSet | null;
  /** Of a supplier proposal: the proposed lines the buyer declined. */
  declinedLineIds?: string[];
  totalBefore?: number;
  totalAfter?: number | null;
  approvalRequestId: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  appliedAt: string | null;
  createdBy: string;
  createdAt: string;
}
/** Before the order reaches the supplier. */
export const notYetOrdered: OrderStatus[] = ['DRAFT', 'PENDING_APPROVAL', 'APPROVED'];
export type BillingStatus = 'NOT_BILLED' | 'PARTIALLY_BILLED' | 'BILLED' | 'OVER_BILLED';
export const billingStatusLabels: Record<BillingStatus, string> = {
  NOT_BILLED: 'Not billed',
  PARTIALLY_BILLED: 'Partially billed',
  BILLED: 'Fully billed',
  OVER_BILLED: 'Over billed',
};
export interface OrderHeader {
  businessUnitId?: string | null;
  projectId?: string | null;
  requestedById?: string | null;
  buyerId?: string | null;
  requiredBy?: string | null;
  supplierReference?: string | null;
  paymentTermsSnapshot?: string | null;
  internalNotes?: string | null;
  taxMode?: 'SUPPLIER_CHARGED' | 'EXEMPT_RESALE';
  /** A key from the company's order types. */
  orderType?: string | null;
  /** The technician the order is for. */
  technicianId?: string | null;
  /** The order's cost is charged to that technician's pay. */
  impactsTechnicianPayroll?: boolean;
  /** The date the order is dated (YYYY-MM-DD). */
  orderDate?: string | null;
  shipToKind?: ShipToKind | null;
  /** The warehouse it ships to, when `shipToKind` is WAREHOUSE. */
  deliveryLocationId?: string | null;
}
/** Where the supplier delivers. */
export type ShipToKind = 'WAREHOUSE' | 'TRUCK' | 'JOB_SITE' | 'PICKUP' | 'TECHNICIAN' | 'OTHER';
export const shipToLabels: Record<ShipToKind, string> = {
  WAREHOUSE: 'A warehouse',
  TRUCK: 'A truck',
  JOB_SITE: 'The job site',
  PICKUP: 'Pick up at the supplier',
  TECHNICIAN: "The technician's address",
  OTHER: 'Another address',
};
/** A warehouse or a truck: a place that keeps stock. */
export const stockedShipTo = (kind: ShipToKind | '' | null | undefined) =>
  kind === 'WAREHOUSE' || kind === 'TRUCK';
/** Where a delivery was taken: the same choices as the order. */
export type DeliveredToKind = ShipToKind;
export const deliveredToLabels = shipToLabels;
/** A kind of order the company uses: special order, disposal fees... */
export interface OrderType {
  key: string;
  label: string;
  /** Just an amount and what it was for, no item list. */
  amountOnly: boolean;
  active: boolean;
}
/** A job's service address as the job keeps it. */
export interface JobServiceAddress {
  street?: string | null;
  street2?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  postalCode?: string | null;
}
export const formatJobAddress = (address?: JobServiceAddress | null) =>
  address
    ? [
        address.street ?? address.address,
        address.street2,
        [address.city, [address.state, address.zip ?? address.postalCode].filter(Boolean).join(' ')]
          .filter(Boolean)
          .join(', '),
      ]
        .filter((part) => part && String(part).trim())
        .join(', ')
    : '';
/** A technician an order can be for: the schedule's record, login or not. */
export interface OrderTechnician {
  id: string;
  name: string;
  /** Their login, when they have one: it finds their truck. */
  authUserId: string | null;
}
export interface OrderOptions {
  legacyDefaultTerms?: string;
  businessUnits: Array<{ id: string; name: string }>;
  people: Array<{ id: string; displayName: string }>;
  /** Technicians on the schedule, with or without a login. */
  technicians: Array<OrderTechnician>;
  projects: Array<{ id: string; projectNumber: string; name: string }>;
  jobs: Array<{
    id: string;
    jobNumber: string;
    name: string;
    businessUnitId: string | null;
    projectId: string | null;
    /** In search results, to tell a unit's jobs from those with none. */
    businessUnit?: { name: string } | null;
  }>;
  selectedJob:
    | (OrderOptions['jobs'][number] & {
        serviceAddress?: JobServiceAddress | null;
        /** The job's project, when the viewer may see it. */
        project?: { id: string; name: string; projectNumber: string } | null;
        /** Who leads the job's next visit: who an order for it is usually for. */
        nextVisitTechnician?: (OrderTechnician & { visitStartAt: string | null }) | null;
      })
    | null;
  currentUser: OrderOptions['people'][number] | null;
  defaultBusinessUnitId: string | null;
  /** Every order type, retired ones too, so old orders keep their label. */
  orderTypes: OrderType[];
  /** The company's address, for a warehouse that has none of its own. */
  companyAddress?: string | null;
}
export interface Order extends OrderHeader {
  source?: 'MANUAL' | 'REQUIREMENT' | 'REPLENISHMENT' | 'FIELD' | 'API' | 'AI';
  businessUnit?: { id: string; name: string } | null;
  project?: { id: string; name: string; projectNumber: string } | null;
  requester?: { id: string; displayName: string } | null;
  buyerUser?: { id: string; displayName: string } | null;
  /** The technician record the order is for. */
  technician?: { id: string; name: string } | null;
  deliveryLocation?: { id: string; code: string; name: string } | null;
  /** Fully billed and every matched bill paid. */
  paid?: boolean;
  /** When the order was last emailed to the supplier. */
  lastSentAt?: string | null;
  /** What prints: the snapshot of the terms chosen. */
  termsAndConditions: string;
  /** The company's default purchase terms, library terms stacked, or its own. */
  termsMode?: TermsMode;
  termsTemplateIds?: string[];
  costsVisible?: boolean;
  billsVisible?: boolean;
  id: string;
  orderNumber: string;
  vendorName: string;
  supplierId: string | null;
  currency: string;
  supplier?: {
    name: string;
    accountNumber?: string | null;
    email?: string | null;
    phone?: string | null;
    /** Supplier contacts copied on the order, as of the last save. */
    copyTo?: Array<{ name: string; email: string }>;
  } | null;
  buyer?: { name: string; address?: string; email?: string; phone?: string } | null;
  closureReason?: string | null;
  reconciliationRequired: boolean;
  approvedAt?: string | null;
  approvedAmount?: number | null;
  /** Why the last approval request was rejected. */
  rejectionReason?: string | null;
  sentAt?: string | null;
  sentVia?: 'EMAIL' | 'MANUAL' | null;
  sentTo?: string[];
  supplierAckStatus?: 'NONE' | 'ACKNOWLEDGED' | 'CHANGES_PROPOSED' | 'REJECTED';
  /** Change orders applied since the order was placed. */
  revisionNumber?: number;
  revisions?: OrderRevision[];
  /** The supplier confirmed the cancellation from its link. */
  cancellationConfirmedAt?: string | null;
  canceledAt?: string | null;
  supplierConfirmationNumber?: string | null;
  supplierAckNote?: string | null;
  acknowledgedAt?: string | null;
  /** The delivery marked as arrived, ahead of reconciling what came. */
  arrivedAt?: string | null;
  shipmentCarrier?: string | null;
  shipmentTracking?: string | null;
  shippedAt?: string | null;
  sends?: Array<{
    id: string;
    kind?: 'ORDER' | 'CANCELLATION' | 'PROPOSAL_ANSWER';
    /** For a PROPOSAL_ANSWER: the supplier proposal it answers. */
    revisionId?: string | null;
    to: string[];
    cc: string[];
    sentAt: string | null;
    createdAt: string;
  }>;
  /** Absent when bills are hidden from the viewer. */
  billingStatus?: BillingStatus;
  matches?: Array<{
    active: boolean;
    removedAt: string | null;
    removalReason: string | null;
    id: string;
    expenseId: string | null;
    reference: string;
    total: number;
    shipping: number;
    tax: number;
    otherCharges: number;
    discount: number;
    lines: Array<{ lineId: string; quantity: number; unitCost: number }>;
    expense: {
      id: string;
      name: string;
      status: string;
      total: number;
      isDeleted: boolean;
    } | null;
    bill: {
      id: string;
      number: string;
      supplierInvoiceNumber: string;
      status: string;
    } | null;
  }>;

  jobId: string | null;
  shipToLocationId?: string | null;
  status: OrderStatus;
  version: number;
  fieldPaymentMethod?: 'HOUSE_ACCOUNT' | 'COMPANY_CARD' | 'PERSONAL_REIMBURSABLE' | null;
  appointmentId?: string | null;
  receiptDueAt?: string | null;
  receiptConfirmedAt?: string | null;
  fieldReviewReasons?: string[];
  supplierReference?: string | null;
  expectedAt: string | null;
  orderedAt: string | null;
  createdAt: string;
  deliveryAddress: string | null;
  notes: string | null;
  total: number;
  shipping: number;
  tax: number;
  otherCharges: number;
  discount: number;
  cancellationReason: string | null;
  /** The order the unreceived balance was moved to. */
  rolledOverTo?: { id: string; orderNumber: string } | null;
  /** The pick list the order was made for; its delivery fills the list. */
  pickList?: {
    id: string;
    number: string;
    fromLocation: { id: string; name: string; code: string; type: string };
  } | null;
  /** The order this one carries the balance of. */
  rolledOverFrom?: Array<{ id: string; orderNumber: string }>;
  lines: OrderLine[];
  job?: { id: string; jobNumber: string; name: string } | null;
  receipts?: Array<{
    id: string;
    requestId?: string;
    kind: 'DELIVERY' | 'RETURN' | 'CORRECTION';
    receivedAt: string;
    receivedBy: string;
    /** Where the delivery was taken, as the order said or as chosen at receipt. */
    deliveredToKind?: DeliveredToKind | null;
    deliveredTo?: string | null;
    /** Set once the delivery was reconciled: put away or consumed, line by line. */
    reconciledAt?: string | null;
    reconciledBy?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    locationAccuracy?: number | null;
    reference: string | null;
    notes: string | null;
    lines: Array<{
      id?: string;
      lineId: string;
      name: string;
      sku?: string | null;
      quantity: number;
      unitCost: number;
      /** Where stocked items went. */
      locationId?: string | null;
      /** STOCK: put away at `locationId`; CONSUMED: used, not stocked. */
      disposition?: 'STOCK' | 'CONSUMED' | null;
    }>;
  }>;
}
export interface DraftLine {
  id?: string;
  purchaseUnit?: string;
  stockUnit?: string;
  unitsPerPurchaseUnit?: number;
  landedChargeOverride?: number | null;
  requestedStockQuantity?: number;
  manufacturerPartNumber?: string;
  partRequirementId?: string;
  name: string;
  sku: string;
  supplierPartNumber?: string;
  itemId?: string;
  /** The job this line is for when not the order's; empty follows the order. */
  jobId?: string | null;
  /** How to show that job when it is not among the loaded choices. */
  jobLabel?: string;
  quantity: number;
  unitCost: number;
}
export interface DraftOrder extends OrderHeader {
  termsAndConditions?: string;
  termsMode?: TermsMode;
  termsTemplateIds?: string[];
  currency?: string;
  supplierId: string;
  jobId?: string;
  /** The warehouse or truck a stock order is received into. */
  shipToLocationId?: string | null;
  expectedAt?: string;
  deliveryAddress: string;
  notes: string;
  shipping: number;
  tax: number;
  otherCharges: number;
  discount: number;
  lines: DraftLine[];
  version?: number;
}
export interface OrderList {
  companyDate: string;
  costsVisible?: boolean;
  rows: Order[];
  count: number;
  summary: {
    drafts: number;
    awaitingApproval?: number;
    awaitingDelivery: number;
    overdue: number;
    fieldReview?: number;
    /** Placed orders the supplier has acknowledged. */
    acknowledged?: number;
    outstandingByCurrency: Array<{ currency: string; value: number }>;
  };
}
export const amount = (n: number) =>
  n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
export const unitCostAmount = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 });
export const lineTotal = (quantity: number, unitCost: number) => {
  if (!Number.isFinite(quantity) || !Number.isFinite(unitCost)) return Number.NaN;
  // Quantity has four decimals and unit cost six. Multiply scaled integers
  // before rounding the extended amount to cents, including half-cent ties.
  const product = BigInt(Math.round(quantity * 10_000)) * BigInt(Math.round(unitCost * 1_000_000));
  const magnitude = product < 0n ? -product : product;
  const cents = (magnitude + 50_000_000n) / 100_000_000n;
  return Number(product < 0n ? -cents : cents) / 100;
};
export const orderDate = (value: string | null) =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      }).format(new Date(value))
    : 'Not set';
export function usePurchasingAuthKey() {
  const userId = useAuthStore((state) => state.userId);
  const permissionsVersion = useAuthStore((state) => state.permissionsVersion);
  return [getActiveTenantQueryKey(), userId, permissionsVersion] as const;
}

export function useOrders(
  search: string,
  status: string,
  page: number,
  jobId?: string,
  filters: {
    supplierId?: string;
    from?: string;
    to?: string;
    queue?: 'AWAITING_DELIVERY' | 'OVERDUE' | 'BILLABLE' | 'FIELD_REVIEW';
    /** The supplier's answer on a placed order. */
    supplierAck?: SupplierAckFilter;
  } = {},
) {
  const authKey = usePurchasingAuthKey();
  return useQuery({
    queryKey: ['purchase-orders', ...authKey, { search, status, page, jobId, ...filters }],
    queryFn: () =>
      apiGet<OrderList>(operationsClient, '/purchase-orders', {
        params: {
          search: search || undefined,
          status: status || undefined,
          page,
          jobId,
          ...filters,
        },
      }),
    refetchInterval: 30_000,
  });
}
export function useOrder(id: string) {
  const authKey = usePurchasingAuthKey();
  return useQuery({
    queryKey: ['purchase-orders', ...authKey, id],
    queryFn: () => apiGet<Order>(operationsClient, `/purchase-orders/${id}`),
  });
}
export const usePurchasingPending = () => useIsMutating({ mutationKey: ['purchasing'] }) > 0;

export function usePurchasingMutation() {
  const batchKeys = useRef(new Map<string, string>());
  const client = useQueryClient();
  const authKey = usePurchasingAuthKey();
  return useMutation({
    mutationKey: ['purchasing'],
    mutationFn: ({ path, body, edit }: { path: string; body: unknown; edit?: boolean }) => {
      if (path === '/purchase-orders/for-job') {
        const key = JSON.stringify([authKey, body]);
        const requestId = batchKeys.current.get(key) ?? createUuid();
        batchKeys.current.set(key, requestId);
        return apiPost<Order>(operationsClient, path, { ...(body as object), requestId });
      }
      return edit
        ? apiPatch<Order>(operationsClient, path, body)
        : apiPost<Order>(operationsClient, path, body);
    },
    onSuccess: async (result, variables) => {
      if (variables.path === '/purchase-orders/for-job')
        batchKeys.current.delete(JSON.stringify([authKey, variables.body]));
      // Keep the form pending until the displayed order has the saved version.
      await Promise.all([
        client.invalidateQueries({ queryKey: ['purchase-orders'] }),
        // Invalidating refetches only what is on screen, and the full-page
        // editor takes the order's place while it saves: fetch the order
        // itself, or it opens at its old version (and a PDF download asks for
        // that version).
        result?.id
          ? client.refetchQueries({
              queryKey: ['purchase-orders', ...authKey, result.id],
              exact: true,
            })
          : undefined,
        client.invalidateQueries({ queryKey: ['part-requirements'] }),
        client.invalidateQueries({ queryKey: ['operational-insights'] }),
        // Placing or editing an order can open or replace an approval request.
        client.invalidateQueries({ queryKey: ['approvals'] }),
      ]);
    },
    onError: (error: Error) => toast.error(error.message || 'Could not save your changes'),
  });
}

export function useMatchableExpenses(orderId: string, page: number) {
  const authKey = usePurchasingAuthKey();
  return useQuery({
    queryKey: ['purchase-orders', ...authKey, orderId, 'matchable-expenses', page],
    queryFn: () =>
      apiGet<{
        data: Array<{
          id: string;
          name: string;
          status: string;
          total: number;
          availableAmount: number;
          supplierId: string;
        }>;
        page: number;
        hasMore: boolean;
      }>(operationsClient, `/purchase-orders/${orderId}/matchable-expenses`, { params: { page } }),
  });
}
export function useExpensePurchaseOrders(expenseId: string | null, enabled: boolean) {
  const authKey = usePurchasingAuthKey();
  return useQuery({
    queryKey: ['purchase-orders', ...authKey, 'expense', expenseId],
    enabled: !!expenseId && enabled,
    queryFn: () =>
      apiGet<Array<{ id: string; orderNumber: string }>>(
        operationsClient,
        `/purchase-orders/expenses/${expenseId}/allocations`,
      ),
  });
}
